/* eslint-disable complexity */
import React, { Component } from 'react';

import cn from 'classnames';

import { isEqual, noop, omit } from 'lodash';
import { node, func, bool, oneOf, shape, string, oneOfType } from 'prop-types';
import { Helmet } from 'react-helmet';

import { connect } from 'react-redux';

import 'react-image-gallery/styles/css/image-gallery.css';

import { withState } from 'recompose';

import AppBar from 'components/Common/LayoutV2/AppBar';
import SideNav from 'components/Common/LayoutV2/SideNav';
import Notification from 'components/Common/Notification';
import Snackbar from 'components/Common/Snackbar';
import FileInput from 'components/FileInput';
import Modal from 'components/Modals';
import { COMPANY_DESCRIPTION, COMPANY_NAME } from 'constants/texts';
import withRouter from 'containers/withRouter';
import withUserTypeAndOrgInfo from 'containers/withUserTypeAndOrgInfo';

import withMediaInfo from 'HOC/withMediaInfo';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';

import s from './LayoutV2.css';

@withUserTypeAndOrgInfo
@withMediaInfo()
@withRouter
@withState('appbarOpen', 'setAppbarOpen', false)
@connect(
  ({ notification, modals: { modalType } }) => ({
    hasBannerNotification: notification.open,
    modalType,
  }),
  null,
)
class LayoutV2 extends Component {
  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  toggleAppbar = () => {
    const { appbarOpen, setAppbarOpen } = this.props;
    setAppbarOpen(!appbarOpen);
  };

  render() {
    const {
      children,
      className,
      style,
      appBar,
      sideNav,
      color,
      hasBannerNotification,
      responsive,
      legacyModule,
      overflow,
      dispatch,
      showBackButton,
      appBarPageDetails,
      mediaInfo,
      isOnboarding,
      containerId,
      appbarOpen,
      modalType,
      ...internal
    } = this.props;

    const { isMobile } = mediaInfo || {};

    const {
      router: { navigate },
      staticContext,
      flashMessage,
      ...restProps
    } = internal;

    return (
      <div
        className={cn(
          responsive && !legacyModule ? s.responsiveLayout : s.layout,
          {
            [s.hasAppBar]: appBar,
            [s.hasSideNav]: sideNav && !isMobile,
          },
          className,
        )}
        style={{ ...style }}
        {...omit(restProps, ['moduleAccess', 'user'])}
      >
        <Helmet
          defaultTitle={COMPANY_NAME}
          titleTemplate={`%s - ${COMPANY_NAME}`}
        >
          {appBarPageDetails?.title && (
            <title>{appBarPageDetails?.title}</title>
          )}
          <meta name="description" content={COMPANY_DESCRIPTION} />
        </Helmet>

        {appBar ? (
          <AppBar
            responsive={responsive}
            sideNav={sideNav}
            isMobile={isMobile}
            color={color}
            toggleAppbar={this.toggleAppbar}
            showBackButton={showBackButton}
            appBarPageDetails={appBarPageDetails}
            isOnboarding={isOnboarding}
          />
        ) : null}
        {sideNav ? (
          <SideNav
            responsive={responsive}
            isMobile={isMobile}
            color={color}
            appbarOpen={appbarOpen}
            toggleAppbar={this.toggleAppbar}
            push={navigate}
          />
        ) : null}
        {/* To allow screen reader access while also accounting for z-index,
            snackbar will either render here if no modals are open or within
            Modal/ModalV2 if a modal is open */}
        {!modalType && <Snackbar />}
        <Notification />
        <Modal />
        <FileInput />

        <div
          className={cn(
            {
              [s.responsiveChildrenContainer]:
                responsive && overflow && !isMobile && !legacyModule,
              [s.mobileResponsiveChildrenContainer]:
                responsive && overflow && isMobile && !legacyModule,
              [s.responsiveChildrenContainerNoOverflow]:
                responsive && !overflow && !isMobile && !legacyModule,
              [s.responsiveChildrenContainerHasAppBar]: appBar && !legacyModule,
              [s.responsiveChildrenContainerHasSideNav]:
                sideNav && !isMobile && !legacyModule,
              [s.legacyModule]: legacyModule,
              [s.extraSpacingTop]: hasBannerNotification && legacyModule,
            },
            className,
          )}
          id={containerId}
        >
          {children}
        </div>
      </div>
    );
  }
}

LayoutV2.propTypes = {
  children: node,
  dispatch: func,
  appBar: bool,
  sideNav: bool,
  color: oneOf([
    'green',
    'red',
    'blue',
    'orange',
    'yellow',
    'indigo',
    'teal',
    'blueSteel',
    'violet',
    'purple',
  ]),
  style: shape({}),
  className: string,
  responsive: bool,
  legacyModule: bool,
  hasBannerNotification: bool,
  overflow: bool,
  showBackButton: oneOfType([bool, func]),
  appBarPageDetails: shape({
    title: string,
    module: string,
    modal: string,
    classes: shape({
      container: string,
      title: string,
    }),
  }),
  isOnboarding: bool,
  router: RouterPropTypes,
  containerId: string,
  user: shape({}),
  mediaInfo: shape({}).isRequired,
  appbarOpen: bool,
  setAppbarOpen: func,
  modalType: string,
};

LayoutV2.defaultProps = {
  appBar: false,
  sideNav: false,
  color: 'green',
  style: {},
  className: '',
  responsive: true,
  showBackButton: false,
  legacyModule: false,
  overflow: true,
  appBarPageDetails: null,
  hasBannerNotification: false,
  dispatch: noop,
  isOnboarding: false,
  containerId: null,
  router: null,
  user: null,
  children: null,
  appbarOpen: false,
  setAppbarOpen: noop,
  modalType: null,
};

export default LayoutV2;
